import type { MarketingTranslationKeys } from "../../types.js";

export const marketing: MarketingTranslationKeys = {
  meta: {
    defaultTitle: "HomeExpense — Clareza financeira para sua família",
    defaultDescription:
      "Gerencie despesas domésticas com rateio inteligente, digitalização de IA, orçamentos e um cofre de garantia.",
    featuresTitle: "Recursos - HomeExpense",
    featuresDescription:
      "Proporção, OCR com tecnologia de IA, diário de despesas, orçamentos e um cofre de garantia para sua casa.",
    methodologyTitle: "Metodologia - HomeExpense",
    methodologyDescription:
      "Como pensamos sobre o produto: precisão, estabilidade a longo prazo e uma UI que mantém o foco nos números.",
    legalTitle: "Jurídico e contato - HomeExpense",
    legalDescription:
      "Privacidade, termos, segurança e como entrar em contato conosco. ",
    privacyTitle: "Política de privacidade — HomeExpense",
    privacyDescription:
      "Responsável pelo tratamento, dados recolhidos, finalidades, conservação, subencargados e os seus direitos.",
    termsTitle: "Termos de utilização — HomeExpense",
    termsDescription:
      "Condições gerais do site e das apps HomeExpense: conta, uso aceitável, propriedade intelectual e limitações.",
    pricingTitle: "Preços - HomeExpense",
    pricingDescription:
      "Planos Home, Smart Scan e Insights: recursos, limites de IA e preços de referência para famílias.",
  },
  nav: {
    ariaMain: "Navegação principal",
    brand: "Despesas domésticas",
    home: "Lar",
    features: "Características",
    methodology: "Metodologia",
    pricing: "Preços",
    legal: "Jurídico e contato",
    register: "Criar uma conta",
    login: "Entrar",
    openMenu: "Abrir menu",
    closeMenu: "Fechar menu",
  },
  footer: {
    tagline: "Gerenciamento claro de despesas domésticas.",
    platformTitle: "Produto",
    legalTitle: "Jurídico",
    supportTitle: "Apoiar",
    privacy: "Privacidade",
    terms: "Termos",
    security: "Segurança",
    contact: "Contato",
    copyright: "© {{year}} HomeExpense. ",
  },
  images: {
    landingHeroAlt: "Painel financeiro mínimo em um tablet, tons neutros com detalhes em azul.",
    landingVizAlt: "Visualização abstrata de dados financeiros em tons marinho, esmeralda e dourado.",
    featuresHeroAlt: "Secretária com papelaria financeira, luz natural, linhas limpas.",
    featuresProrateAlt: "Tablet mostrando gráficos e métricas de divisão de despesas.",
    featuresAiAlt: "Telefone digitalizando um recibo com linhas de destaque digital.",
    legalMapAlt: "Mapa estilizado em escala de cinza de uma grade de cidade.",
    methodologyHeroAlt: "Detalhe arquitetônico mínimo, linhas limpas e sombras suaves.",
    methodologyOfficeAlt: "Interior de escritório moderno com luz natural e mobiliário minimalista.",
  },
  home: {
    heroEyebrow: "Finanças domésticas",
    heroTitleLine1: "Clareza financeira",
    heroTitleLine2: "para sua casa",
    heroDescription:
      "Rateio inteligente, um diário de despesas, orçamentos, digitalização de IA e um cofre de garantia – criado para residências e residências compartilhadas.",
    ctaPrimary: "Crie uma conta gratuita",
    ctaSecondary: "Explore nossa metodologia",
    cardSurplusLabel: "Exemplo de almofada mensal",
    cardSurplusValue: "1.240,00€",
    highlightsTitle: "Por que HomeExpense",
    highlight1Label: "Foco",
    highlight1Value: "Famílias compartilhadas",
    highlight2Label: "Preços",
    highlight2Value: "Nível gratuito",
    highlight3Label: "Produto",
    highlight3Value: "Melhorias contínuas",
    highlight4Label: "Privacidade",
    highlight4Value: "Seus dados, sua conta",
    featuresEyebrow: "Áreas principais do aplicativo",
    featuresTitle: "Recursos que correspondem a fluxos de trabalho reais",
    featuresSubtitle:
      "Os mesmos pilares que você verá no aplicativo: despesas, orçamento, digitalização e cofre – com divisões justas entre os membros.",
    featureProrateTitle: "Rateio inteligente",
    featureProrateDesc:
      "Aloque custos recorrentes e únicos entre membros, porcentagens e regras domésticas.",
    featureOcrTitle: "Varredura de IA",
    featureOcrDesc:
      "Faça upload dos recibos e deixe o sistema extrair o valor, o comerciante e uma categoria sugerida para você confirmar.",
    featureVaultTitle: "Cofre de garantia",
    featureVaultDesc:
      "Acompanhe os ativos residenciais com datas de compra e garantia, lembretes e documentos organizados.",
    visualizeTitle: "Visibilidade do orçamento",
    visualizeSubtitle:
      "Barras de progresso e visualizações mensais para metas reservadas, gastas e de categoria.",
    bar1Label: "Impostos trimestrais (amostra)",
    bar1Value: "65% reservado",
    bar2Label: "Fundo de renovação (amostra)",
    bar2Value: "42% da meta",
    ctaBottomTitle: "Pronto para começar?",
    ctaBottomSubtitle: "Crie sua conta em segundos e abra o painel para configurar sua casa.",
    ctaBottomButton: "Cadastre-se gratuitamente",
  },
  featuresPage: {
    heroEyebrow: "Produto",
    heroTitle: "Ferramentas para o moderno",
    heroTitleItalic: "doméstico",
    heroDescription:
      "Mais do que uma lista: um diário com filtros, orçamentos rateados, digitalização de IA e um cofre para garantias e documentos.",
    f01Label: "Recurso 01",
    f01Title: "Rateio inteligente",
    f01Body:
      "Alinhe as despesas com as datas de mudança, mudanças de associação e as regras que você definiu para a casa.",
    f01aTitle: "Lógica com reconhecimento de data",
    f01aBody: "Útil quando alguém ingressa no meio do mês ou as contribuições mudam.",
    f01bTitle: "Divisões ponderadas",
    f01bBody: "Percentagens por membro com base no que o agregado familiar concordou.",
    profileLabel: "Perfil dividido (amostra)",
    demoRowALabel: "Zona A",
    demoRowAPct: "42%",
    demoRowBLabel: "Zona B",
    demoRowBPct: "28%",
    demoRowCLabel: "Áreas comuns",
    demoRowCPct: "30%",
    f02Label: "Recurso 02",
    f02Title: "IA e OCR",
    f02Body:
      "Transforme recibos em papel em campos estruturados: valor, data, comerciante e uma categoria sugerida.",
    f02Quote:
      "O fluxo foi projetado para que você possa revisar e salvar com apenas alguns toques após a digitalização.",
    f02QuoteAttribution: "- Equipe HomeExpense",
    overlayComplete: "Análise concluída",
    overlayCategoryLine: "Categoria sugerida • quantidade detectada",
    f03Eyebrow: "Recurso 03",
    f03Title: "Cofre de garantia",
    f03Subtitle:
      "Registre dispositivos e ativos com datas, documentos e lembretes antes do término da cobertura.",
    vaultB1Title: "Lembretes de expiração",
    vaultB1Body: "Fique à frente das janelas de manutenção e das datas de término da garantia.",
    vaultChip1: "Notificações",
    vaultChip2: "Resumos por e-mail",
    vaultB2Title: "Documentação pronta para reivindicação",
    vaultB2Body: "Mantenha as provas e os detalhes dos ativos à mão quando precisar deles.",
    vaultB3Title: "Ativos identificáveis",
    vaultB3Body: "Limpe as referências por nome, tipo e datas fixadas.",
    vaultB4Title: "Histórico vinculado",
    vaultB4Body: "Anexe notas de serviço ou faturas ao mesmo registro de ativo.",
    ctaTitle: "Comece hoje",
    ctaSubtitle: "Crie sua conta e configure sua casa em minutos.",
    ctaPrimary: "Criar uma conta",
    ctaSecondary: "Leia a metodologia",
  },
  methodologyPage: {
    heroEyebrow: "Nossa metodologia",
    heroTitle: "Despesas domésticas:",
    heroTitleAccent: "clareza de propósito",
    heroDescription:
      "Administrar uma casa é sequenciar decisões ao longo do tempo. ",
    precisionTitle: "Precisão",
    precisionBody:
      "Evitamos atalhos que distorcem os totais: categorias, divisões e o mês familiar permanecem consistentes na interface do usuário e na API.",
    accuracyLabel: "Foco",
    accuracyValue: "Dados consistentes",
    stabilityTitle: "Estabilidade",
    stabilityBody:
      "O orçamento e o diário analisam o mês e o futuro próximo: cobranças recorrentes, rateio e saldo doméstico.",
    growthTitle: "Panorama geral",
    growthSubtitle: "Resumos que apoiam a próxima decisão.",
    riskTitle: "Menos surpresas",
    riskSubtitle: "Garantias e lembretes de obrigações e ativos.",
    narrativeEyebrow: "Experiência",
    narrativeQuote:
      "Uma casa é um sistema – pessoas, despesas e tempo. ",
    narrativeManifesto: "Manifesto do produto",
    pillar1Num: "01",
    pillar1Title: "UI silenciosa",
    pillar1Body: "Hierarquia clara para que o número importante tenha peso visual.",
    pillar2Num: "02",
    pillar2Title: "Dados com narrativa",
    pillar2Body: "Listas, totais e barras que explicam o que aconteceu e o que vem a seguir.",
    pillar3Num: "03",
    pillar3Title: "Ferramentas domésticas",
    pillar3Body: "Casas, membros, orçamento, cofre e digitalização em um único fluxo.",
    breakTitle: "Construído para quem administra as finanças domésticas.",
    breakCta: "Explorar recursos",
  },
  legalPage: {
    heroEyebrow: "Compromisso",
    heroTitle: "Privacidade, segurança e",
    heroTitleAccent: "conversa aberta",
    heroDescription:
      "Encontre ligações à política de privacidade e aos termos de utilização, informação de segurança e como nos contactar.",
    controllerName: "GProjectdevelop LLC (Estados Unidos).",
    controllerTaxNote:
      "A sociedade encontra-se em processo de constituição; os identificadores fiscais definitivos serão publicados aqui quando disponíveis.",
    controllerPresence:
      "Equipa distribuída entre Miami (EUA) e Astúrias (Espanha), sem uma morada única de escritório presencial.",
    privacyTitle: "Privacidade",
    privacyLead:
      "Não vendemos seus dados financeiros. ",
    privacyBullet1: "HTTPS para tráfego de cliente.",
    privacyBullet2: "Os espaços domésticos exigem sua conta autenticada.",
    privacyBullet3: "Direito de solicitar a exclusão de acordo com a lei aplicável.",
    privacyReadMore: "Ver a política de privacidade completa",
    termsSectionLabel: "Acordo",
    termsTitle: "Termos de serviço",
    termsBody:
      "Estes termos regulam o acesso e uso do site e das aplicações HomeExpense. Recomendamos ler o documento completo.",
    termsUpdatedLabel: "Última atualização",
    termsUpdatedValue: "10 de maio de 2026",
    termsCta: "Ver termos de utilização",
    securityTitle: "Segurança",
    securityBody:
      "Infraestrutura e práticas alinhadas com as expectativas comuns de SaaS, fortalecidas onde a API e a autenticação exigem.",
    securityChip1: "HTTPS",
    securityChip2: "Autenticação segura",
    contactTitle: "Contato",
    contactLead:
      "Para suporte, privacidade ou dados pessoais, escreva-nos ou use o formulário; pedidos de proteção de dados podem usar o mesmo canal.",
    contactMapTitle: "Presença",
    contactMapBody:
      "Equipa distribuída entre Miami (Flórida, EUA) e Astúrias (Espanha). Não temos um balcão único com morada fixa.",
    formName: "Nome completo",
    formNamePlaceholder: "Seu nome",
    formEmail: "E-mail",
    formEmailPlaceholder: "você@email.com",
    formMessage: "Mensagem",
    formMessagePlaceholder: "Como podemos ajudar?",
    formSubmit: "Enviar mensagem",
    formSubmitting: "A enviar…",
    formSuccess: "Mensagem enviada. Responderemos em breve.",
    formErrorGeneric: "Não foi possível enviar a mensagem. Tente novamente mais tarde.",
    formErrorValidation: "Verifique o nome, o e-mail e a mensagem (mínimo 10 caracteres).",
    formErrorConfig: "O envio do formulário não está configurado neste ambiente.",
  },
  privacyPage: {
    documentEyebrow: "Transparência",
    documentTitle: "Política de privacidade",
    lastUpdatedLabel: "Última atualização",
    lastUpdatedValue: "10 de maio de 2026",
    intro:
      "Na HomeExpense levamos a privacidade a sério. Esta política descreve, de forma geral, que dados pessoais podemos tratar quando utiliza o nosso site ou aplicações móveis, com que finalidades e que direitos tem. O serviço pode evoluir; se reside no Espaço Económico Europeu ou noutras regiões com regras específicas, alguns pontos podem exigir informação adicional que pode solicitar pelos contactos indicados no final.",
    sectionControllerTitle: "1. Responsável pelo tratamento",
    sectionControllerBody:
      "O responsável pelo tratamento dos dados pessoais obtidos através do produto HomeExpense, em relação a utilizadores e visitantes dos canais oficiais, é a GProjectdevelop LLC, com domicílio nos Estados Unidos (« nós » ou o « responsável »). A entidade pode ainda estar em processo de constituição; os dados de registo ou identificadores fiscais definitivos serão actualizados nesta política quando aplicável. Para qualquer questão relacionada com esta política ou com o exercício de direitos, pode escrever para o endereço indicado na secção de contacto (incluindo contacto de protecção de dados, quando relevante).",
    sectionDataTitle: "2. Dados pessoais que podemos recolher",
    sectionDataIntro:
      "Conforme a sua interação com o serviço, podemos tratar categorias como as seguintes (nem todas se aplicam em cada caso):",
    sectionDataBullet1:
      "Dados de conta e perfil: identificador, nome ou alcunha, correio eletrónico, preferências de idioma ou fuso horário e credenciais de acesso (geridas com medidas de segurança adequadas).",
    sectionDataBullet2:
      "Dados do agregado familiar e financeiros que introduz ou outros membros autorizados: despesas, orçamentos, categorias, documentos ou metadados associados ao cofre, e informação que decida guardar na aplicação.",
    sectionDataBullet3:
      "Dados técnicos e da aplicação: tipo de dispositivo, sistema operativo, versão da app, identificadores técnicos ou de sessão, registos de erros e, quando aplicável, dados agregados de utilização para melhorar a estabilidade do serviço.",
    sectionDataBullet4:
      "Comunicações que nos envie voluntariamente (por exemplo mensagens de suporte) e, se necessário, metadados para gerir a conversa.",
    sectionDataBullet5:
      "Imagens ou ficheiros que carregue para funções como digitalização de recibos; podem incluir dados visíveis no documento. Recomendamos não incluir dados sensíveis desnecessários.",
    sectionPurposeTitle: "3. Finalidades",
    sectionPurposeBody:
      "Tratamos os dados para prestar e manter o serviço HomeExpense (agregados, despesas, orçamento, cofre e funções associadas), autenticar utilizadores, personalizar a experiência na conta, prestar suporte, cumprir obrigações legais aplicáveis, melhorar a segurança e o desempenho, e comunicar consigo sobre o serviço quando necessário. Se no futuro introduzirmos comunicações comerciais opcionais, solicitaremos, quando exigido, consentimento ou ofereceremos um mecanismo claro de oposição, de acordo com a lei aplicável.",
    sectionLegalBasisTitle: "4. Base legal (quadro geral)",
    sectionLegalBasisBody:
      "Conforme o tipo de tratamento e a norma aplicável (por exemplo o Regulamento (UE) 2016/679, «RGPD»), as bases legais podem incluir: execução do contrato ou condições do serviço quando o tratamento for necessário para lhe fornecer a funcionalidade solicitada; interesse legítimo do responsável para garantir segurança, melhoria técnica e prevenção de fraude, devidamente ponderado face aos seus direitos; cumprimento de obrigações legais; e consentimento quando for exigido para finalidades concretas (por exemplo certos cookies não essenciais ou comunicações opcionais). Pode pedir detalhe sobre um tratamento específico pelos contactos abaixo.",
    sectionRetentionTitle: "5. Conservação",
    sectionRetentionBody:
      "Conservamos os dados pelo tempo necessário para cumprir as finalidades descritas e obrigações legais. Os critérios incluem a vigência da sua conta, a necessidade de resolver incidentes ou reclamações, e prazos legais de prescrição ou auditoria. Quando os dados deixarem de ser necessários, procedemos à supressão ou anonimização segundo procedimentos internos e a lei aplicável.",
    sectionThirdPartiesTitle: "6. Subcontratados e serviços de terceiros",
    sectionThirdPartiesBody:
      "Podemos recorrer a fornecedores de infraestrutura, alojamento, correio transaccional, monitorização de erros ou outras ferramentas que actuem como subcontratados em nosso nome, com contratos e medidas de segurança adequados. Também podemos ser obrigados a divulgar dados a autoridades se a lei o exigir. Não vendemos os seus dados pessoais a terceiros. Se transferirmos dados fora do Espaço Económico Europeu, aplicaremos salvaguardas reconhecidas (por exemplo cláusulas contratuais-tipo) quando proceda.",
    sectionRightsTitle: "7. Os seus direitos",
    sectionRightsIntro:
      "Na medida em que lhe assistam face ao responsável, pode exercer os seguintes direitos (salvo excepções legais):",
    sectionRightsBullet1: "Acesso aos seus dados pessoais e informação sobre o tratamento.",
    sectionRightsBullet2: "Rectificação de dados inexatos ou incompletos.",
    sectionRightsBullet3: "Apagamento («direito ao esquecimento») quando aplicável.",
    sectionRightsBullet4: "Limitação do tratamento, oposição e, quando aplicável, portabilidade dos dados que forneceu.",
    sectionRightsBullet5:
      "Retirar o consentimento quando o tratamento se baseie nele, sem afectar a licitude prévia. Também pode apresentar reclamação à autoridade de controlo de protecção de dados competente no seu país.",
    sectionMinorsTitle: "8. Menores",
    sectionMinorsBody:
      "A HomeExpense não se dirige a menores de dezasseis (16) anos. Se é encarregado de educação e acredita que um menor nos forneceu dados pessoais sem o seu consentimento, contacte-nos para podermos adoptar as medidas adequadas.",
    sectionChangesTitle: "9. Alterações a esta política",
    sectionChangesBody:
      "Podemos actualizar esta política para reflectir alterações legais, técnicas ou do produto. Publicaremos a versão vigente nesta página e, quando a alteração for relevante, procuraremos notificá-lo por meios razoáveis (por exemplo aviso na aplicação ou correio). Recomendamos rever a política periodicamente.",
    sectionContactTitle: "10. Contacto",
    sectionContactBody:
      "Para exercer direitos, questões de privacidade ou pedidos de informação adicional sobre o tratamento dos seus dados, escreva para:",
    contactEmail: "contacto@gprojectdevelop.com",
  },
  termsPage: {
    documentEyebrow: "Condições",
    documentTitle: "Termos de utilização",
    lastUpdatedLabel: "Última atualização",
    lastUpdatedValue: "10 de maio de 2026",
    intro:
      "Estes termos regulam o acesso e uso do site home-expense.gprojectdevelop.com, das aplicações móveis HomeExpense e dos serviços associados prestados pela GProjectdevelop LLC (« nós », « o prestador »). Ao criar conta, descarregar a app ou utilizar o serviço, confirma que leu e aceita estes termos. Caso contrário, não utilize o serviço. Trata-se de uma primeira versão orientativa; recomenda-se revisão jurídica.",
    sectionPartyTitle: "1. Partes e estado",
    sectionPartyBody:
      "O serviço é prestado pela GProjectdevelop LLC, com domicílio nos Estados Unidos. A informação societária e fiscal definitiva poderá ser publicada nestas páginas. « Utilizador » é a pessoa singular ou a entidade em nome da qual actua.",
    sectionServiceTitle: "2. Descrição do serviço",
    sectionServiceBody:
      "A HomeExpense oferece ferramentas digitais para organizar despesas do agregado, orçamentos, membros, cofre de documentos e funções auxiliares (incluindo, conforme o plano, capacidades de IA). O serviço é prestado « tal como está » e pode alterar-se. Não somos consultores financeiros, fiscais ou jurídicos; os cálculos e sugestões são meramente informativos.",
    sectionAccountTitle: "3. Conta, idade e segurança",
    sectionAccountBody:
      "Deve fornecer dados de registo correctos e manter as credenciais confidenciais. É responsável pela actividade na sua conta salvo acesso não autorizado comunicado sem demora injustificada. O serviço não se dirige a menores de 16 anos. Podemos recusar o registo, suspender ou encerrar contas por incumprimento grave.",
    sectionAcceptableUseTitle: "4. Uso aceitável",
    sectionAcceptableUseIntro: "Compromete-se a não utilizar o serviço para:",
    sectionAcceptableUseBullet1:
      "Violar leis aplicáveis, infringir direitos de terceiros ou aceder a sistemas sem autorização.",
    sectionAcceptableUseBullet2:
      "Introduzir malware, sobrecarregar a infraestrutura ou sondar vulnerabilidades sem autorização.",
    sectionAcceptableUseBullet3:
      "Revender ou explorar o serviço em concorrência sem acordo escrito.",
    sectionAcceptableUseBullet4:
      "Carregar conteúdo ilícito ou dados pessoais de terceiros sem base legal adequada.",
    sectionContentTitle: "5. Dados e conteúdos",
    sectionContentBody:
      "Conserva a titularidade da informação que introduz. Concede-nos uma licença limitada para a alojar e processar para prestar o serviço, nos termos da política de privacidade.",
    sectionFeesTitle: "6. Planos e preços",
    sectionFeesBody:
      "Algumas funções podem ser gratuitas ou pagas conforme o plano publicado. Os preços podem mudar com aviso razoável. Impostos e encargos de terceiros (lojas de aplicações) podem aplicar-se.",
    sectionIpTitle: "7. Propriedade intelectual",
    sectionIpBody:
      "O software, a marca e os materiais do serviço (excepto o seu conteúdo) pertencem ao prestador ou licenciantes.",
    sectionDisclaimerTitle: "8. Exclusão de garantias",
    sectionDisclaimerBody:
      "Na medida permitida pela lei, o serviço é prestado sem garantias expressas ou implícitas.",
    sectionLiabilityTitle: "9. Limitação de responsabilidade",
    sectionLiabilityBody:
      "Na medida máxima permitida, não seremos responsáveis por danos indirectos ou lucros cessantes. A responsabilidade agregada em doze meses não excederá as taxas pagas por serviços pagos ou, na ausência destas, 50 €, sem prejuízo de mínimos legais imperativos.",
    sectionIndemnityTitle: "10. Indemnização",
    sectionIndemnityBody:
      "Nos indemnizará perante reclamações de terceiros decorrentes do uso indevido do serviço ou do incumprimento destes termos.",
    sectionTerminationTitle: "11. Cessação",
    sectionTerminationBody:
      "Pode deixar de utilizar o serviço e solicitar o encerramento da conta. Podemos suspender o acesso por incumprimento grave ou exigência legal.",
    sectionLawTitle: "12. Lei aplicável",
    sectionLawBody:
      "Salvo normas imperativas do seu país de residência, estes termos regem-se pelas leis dos Estados Unidos e, para litígios relacionados com serviços prestados a partir da Flórida, pelas leis do Estado da Flórida, sem conflito de leis, sem prejuízo de direitos do consumidor na UE.",
    sectionChangesTitle: "13. Alterações",
    sectionChangesBody:
      "Podemos alterar estes termos publicando uma nova versão e actualizando a data de « última actualização ». Alterações relevantes serão comunicadas por meios razoáveis quando possível.",
    sectionContactTitle: "14. Contacto",
    sectionContactBody:
      "Para questões sobre estes termos ou o serviço, escreva para:",
    contactEmail: "contacto@gprojectdevelop.com",
  },
  pricingPage: {
    heroEyebrow: "Planos",
    heroTitle: "Preços simples",
    heroSubtitle:
      "Três níveis: núcleo sem IA, digitalização de recibos de IA com limite diário e insights inteligentes com mais verificações. ",
    leadTitle: "Transparência",
    leadBody:
      "Os níveis pagos refletem o custo estimado de processamento de IA, além de uma margem modesta. ",
    noPaymentNote: "Os pagamentos no aplicativo ainda não estão habilitados – você pode se inscrever e usar o produto sem um fluxo de checkout.",
    priceNote: "Preços de referência; ",
    plan1Name: "Lar",
    plan1Tagline: "Produto central completo, sem recursos de IA",
    plan1Price: "0€",
    plan1Period: "/mês",
    plan1RecurringCaption: "Grátis para sempre",
    plan1F1: "Diário de despesas, rateio, orçamento e cofre de garantia",
    plan1F2: "Membros da família e regras de divisão personalizáveis",
    plan1F3: "Sem digitalização de recibos de IA ou sugestões automatizadas",
    plan2Name: "Verificação inteligente",
    plan2Tagline: "Casa mais digitalização de IA",
    plan2Price: "4,99€",
    plan2Period: "/mês",
    plan2RecurringCaption: "Preço mensal de referência",
    plan2Popular: "Melhor equilíbrio",
    plan2F1: "Tudo em casa",
    plan2F2: "Até 5 verificações de recibos de IA por dia",
    plan2F3: "Extração de valor, comerciante e categoria sugerida",
    plan3Name: "Percepções",
    plan3Tagline: "Mais IA e incentivos financeiros",
    plan3Price: "12,99€",
    plan3Period: "/mês",
    plan3RecurringCaption: "Preço mensal de referência",
    plan3F1: "Tudo na verificação inteligente",
    plan3F2: "Até 15 varreduras de IA por dia",
    plan3F3: "Sugestões inteligentes sobre padrões e oportunidades de gastos",
    cta: "Criar uma conta",
    includesLabel: "Inclui",
  },
};
